import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { LanguageContext } from '../contexts/LanguageContext';
import { getUser, clearAuth } from '../utils/auth';
import { queryClient } from '../lib/queryClient';
import { palette, typography, theme } from '../theme/colors';

const DRAWER_WIDTH = 220;
const HEADER_HEIGHT = 56;

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header = ({ onMenuClick }: HeaderProps) => {
  const { mode, setMode } = useContext(ColorModeContext);
  const { language, setLanguage, supportedLanguages } = useContext(LanguageContext);
  const user = getUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    surfaceHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    surfaceHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    clearAuth();
    queryClient.clear();
    navigate('/login');
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleLanguageCycle = () => {
    const currentIndex = supportedLanguages.indexOf(language);
    const nextIndex = (currentIndex + 1) % supportedLanguages.length;
    setLanguage(supportedLanguages[nextIndex]);
  };

  const getLanguageLabel = (lang: string) => {
    return lang.toUpperCase();
  };

  const iconButtonStyles = {
    width: 34,
    height: 34,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    transition: theme.transitions.fast,
    '&:hover': {
      backgroundColor: alpha(colors.textSecondary, 0.08),
      color: colors.textPrimary,
    },
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        left: { xs: 0, sm: DRAWER_WIDTH },
        height: HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        backgroundColor: colors.bg,
        borderBottom: `1px solid ${colors.border}`,
        zIndex: 1100,
      }}
    >
      {/* Left Side - Mobile Menu Button */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={onMenuClick}
          sx={{
            display: { sm: 'none' },
            ...iconButtonStyles,
          }}
        >
          <MenuIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
        {/* Theme Toggle */}
        <Tooltip title={mode === 'dark' ? 'LIGHT MODE' : 'DARK MODE'} arrow>
          <IconButton
            onClick={() => setMode()}
            sx={iconButtonStyles}
          >
            {mode === 'light' ? (
              <LightModeOutlinedIcon sx={{ fontSize: 18 }} />
            ) : (
              <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Box
          sx={{
            width: '1px',
            height: 24,
            backgroundColor: colors.border,
            mx: 0.5,
          }}
        />

        {/* User Menu */}
        {user && (
          <>
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                border: `1px solid transparent`,
                backgroundColor: 'transparent',
                transition: theme.transitions.fast,
                '&:hover': {
                  backgroundColor: alpha(colors.textSecondary, 0.05),
                  borderColor: colors.border,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: palette.primary,
                  fontFamily: typography.fontFamily.body,
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: '12px',
                }}
              >
                {user.firstName[0]}{user.lastName[0]}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.body,
                    fontWeight: typography.fontWeight.medium,
                    fontSize: '13px',
                    color: colors.textPrimary,
                    lineHeight: 1.2,
                  }}
                >
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '10px',
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Console
                </Typography>
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  color: colors.textMuted,
                  display: { xs: 'none', md: 'block' },
                }}
              />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    borderRadius: '10px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.surfaceElevated,
                  },
                },
              }}
            >
              {/* User Info */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: palette.primary,
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.semibold,
                      fontSize: '14px',
                    }}
                  >
                    {user.firstName[0]}{user.lastName[0]}
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: typography.fontFamily.body,
                        fontWeight: typography.fontWeight.medium,
                        color: colors.textPrimary,
                        fontSize: '13px',
                      }}
                    >
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: typography.fontFamily.body,
                        color: colors.textMuted,
                        fontSize: '11px',
                      }}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ mt: 1.5 }}>
                  <Chip
                    icon={<AdminPanelSettingsIcon sx={{ fontSize: '14px !important' }} />}
                    label="SUPER ADMIN"
                    size="small"
                    sx={{
                      height: '22px',
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '10px',
                      fontWeight: typography.fontWeight.semibold,
                      backgroundColor: alpha(palette.accent, 0.12),
                      color: palette.accent,
                      border: `1px solid ${alpha(palette.accent, 0.25)}`,
                      '& .MuiChip-icon': {
                        color: palette.accent,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 1, borderColor: colors.border }} />

              <MenuItem
                onClick={handleSettings}
                sx={{
                  mx: 1,
                  borderRadius: '6px',
                  fontFamily: typography.fontFamily.body,
                  fontSize: '13px',
                  '&:hover': {
                    backgroundColor: alpha(colors.textSecondary, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ color: colors.textSecondary, minWidth: 32 }}>
                  <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontFamily: typography.fontFamily.body,
                    fontSize: '13px',
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={handleLanguageCycle}
                sx={{
                  mx: 1,
                  borderRadius: '6px',
                  fontFamily: typography.fontFamily.body,
                  fontSize: '13px',
                  '&:hover': {
                    backgroundColor: alpha(colors.textSecondary, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ color: colors.textSecondary, minWidth: 32 }}>
                  <LanguageOutlinedIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Language"
                  primaryTypographyProps={{
                    fontFamily: typography.fontFamily.body,
                    fontSize: '13px',
                  }}
                />
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: colors.surfaceHover,
                    ml: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '11px',
                      fontWeight: typography.fontWeight.semibold,
                    }}
                  >
                    {getLanguageLabel(language)}
                  </Typography>
                </Box>
              </MenuItem>

              <Divider sx={{ my: 1, borderColor: colors.border }} />

              <MenuItem
                onClick={handleLogout}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '6px',
                  fontFamily: typography.fontFamily.body,
                  fontSize: '13px',
                  '&:hover': {
                    backgroundColor: alpha(palette.error, 0.08),
                    '& .MuiListItemIcon-root': {
                      color: palette.error,
                    },
                    '& .MuiListItemText-primary': {
                      color: palette.error,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: colors.textSecondary, minWidth: 32 }}>
                  <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Sign out"
                  primaryTypographyProps={{
                    fontFamily: typography.fontFamily.body,
                    fontSize: '13px',
                  }}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};
